import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Pencil } from "lucide-react";
import remarkGfm from "remark-gfm";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Textarea } from "~/components/ui/textarea";

interface EditableDescriptionProps {
  value?: string | null;
  canEdit: boolean;
  // Persist the new description. Should throw on failure so the dialog stays
  // open; the caller owns the API call + success/error toasts.
  onSave: (next: string) => Promise<void>;
}

/**
 * Markdown description block shared by the project / dataset / agent overview
 * pages: renders the description (with a fallback), and for editors shows a
 * hover pencil that opens a Markdown editor dialog. Presentational — the caller
 * supplies `onSave`.
 */
export function EditableDescription({ value, canEdit, onSave }: EditableDescriptionProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setDraft(value ?? "");
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setEditOpen(false);
    } catch {
      // Caller surfaces the error toast; keep the dialog open so edits aren't lost.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group relative">
      <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-em:text-foreground">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {value || "_No description provided._"}
        </ReactMarkdown>
      </div>
      {canEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-0 size-7 opacity-0 transition-opacity group-hover:opacity-100"
          aria-label="Edit description"
          onClick={openEdit}
        >
          <Pencil className="size-3.5" />
        </Button>
      )}

      <Dialog open={editOpen} onOpenChange={(open) => !open && setEditOpen(false)}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-2xl" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Edit description</DialogTitle>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
            <p className="text-xs text-muted-foreground">
              Supports Markdown: <strong>**bold**</strong>, <em>*italic*</em>, lists, and more.
            </p>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={12}
              className="font-mono text-sm focus-visible:ring-0"
              disabled={saving}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
